import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  useGetVansQuery,
  useCreateVanMutation,
  useUpdateVanMutation,
  useDeactivateVanMutation,
} from '../../../store/api/vanSalesApi';
import { useGetUsersQuery } from '../../../store/api/userApi';

function VansList() {
  const navigate = useNavigate();
  const { data: vans = [], isLoading } = useGetVansQuery();
  const { data: usersData } = useGetUsersQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];
  const [createVan] = useCreateVanMutation();
  const [updateVan] = useUpdateVanMutation();
  const [deactivateVan] = useDeactivateVanMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVan, setEditingVan] = useState(null);
  const [form, setForm] = useState({ name: '', plateNumber: '', driverId: '' });

  const openCreate = () => {
    setEditingVan(null);
    setForm({ name: '', plateNumber: '', driverId: '' });
    setDialogOpen(true);
  };

  const openEdit = (van) => {
    setEditingVan(van);
    setForm({ name: van.name, plateNumber: van.plateNumber, driverId: van.driverId || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.plateNumber) {
      alert('Name and Plate Number are required');
      return;
    }
    try {
      if (editingVan) {
        await updateVan({ id: editingVan.id, ...form }).unwrap();
      } else {
        await createVan(form).unwrap();
      }
      setDialogOpen(false);
    } catch (err) {
      alert(err?.data?.message || 'Error saving van');
    }
  };

  const handleToggleActive = async (van) => {
    try {
      await deactivateVan(van.id).unwrap();
    } catch (err) {
      alert(err?.data?.message || 'Error updating van');
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Vans
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/van-sales')}>
            Back to Trips
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Van
          </Button>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Plate Number</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Driver</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Location Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vans.map((van) => (
                <TableRow key={van.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{van.name}</TableCell>
                  <TableCell>{van.plateNumber}</TableCell>
                  <TableCell>
                    {van.driver
                      ? `${van.driver.firstName} ${van.driver.lastName}`
                      : '—'}
                  </TableCell>
                  <TableCell>{van.location?.code || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={van.isActive ? 'Active' : 'Inactive'}
                      color={van.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(van)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={van.isActive ? 'Deactivate' : 'Activate'}>
                      <IconButton size="small" onClick={() => handleToggleActive(van)}>
                        {van.isActive ? (
                          <BlockIcon fontSize="small" color="error" />
                        ) : (
                          <CheckCircleIcon fontSize="small" color="success" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {vans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No vans registered. Click "Add Van" to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingVan ? 'Edit Van' : 'Add Van'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Van Name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Plate Number *"
            value={form.plateNumber}
            onChange={(e) => setForm({ ...form, plateNumber: e.target.value })}
            fullWidth
            disabled={!!editingVan}
          />
          <TextField
            select
            label="Driver (optional)"
            value={form.driverId}
            onChange={(e) => setForm({ ...form, driverId: e.target.value })}
            fullWidth
          >
            <MenuItem value="">None</MenuItem>
            {userList.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || u.username}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingVan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VansList;
